import { useEffect, useRef } from "react";
import { useFormContext } from "@jsq/ds/forms";

type FormFieldValue = string | number | boolean | Date | null | undefined;

interface UseAIPopulatedFieldsOptions<T> {
  editMode?: boolean;
  fieldNames?: Array<keyof T>;
  aiExtractionSupported?: boolean;
}

export const useAIPopulatedFields = <T extends Record<string, FormFieldValue>>({
  editMode = false,
  fieldNames,
  aiExtractionSupported: isAiExtractionEnabled = false,
}: UseAIPopulatedFieldsOptions<T> = {}) => {
  const { values, initialValues } = useFormContext<T>();
  const editedFieldsRef = useRef<Set<keyof T>>(new Set());

  useEffect(() => {
    if (editMode || !isAiExtractionEnabled) {
      return;
    }

    const fieldsToTrack = fieldNames ?? Object.keys(values);

    fieldsToTrack.forEach((fieldName) => {
      const currentValue = values[fieldName];
      const initialValue = initialValues[fieldName];

      if (currentValue !== initialValue) {
        editedFieldsRef.current.add(fieldName);
      }
    });
  }, [values, initialValues, editMode, fieldNames, isAiExtractionEnabled]);

  const hasFieldPopulatedByAI = (field: keyof T): boolean => {
    if (editMode || !isAiExtractionEnabled) {
      return false;
    }

    const initialValue = initialValues[field];
    const hasInitialData = initialValue != null && initialValue !== "";
    const hasBeenEdited = editedFieldsRef.current.has(field);

    return hasInitialData && !hasBeenEdited;
  };

  return {
    hasFieldPopulatedByAI,
    editedFields: editedFieldsRef.current,
  };
};
